import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  IUpdateOverlayCutoutPayload,
  OnboardingTooltipId,
  OnboardingTooltipFlow
} from '@src/types/onboardingTooltips.types'
import { getDOMRect } from '@src/utilities/DOMRect'
import { getWindowObject } from '@src/utilities/iframeDetection'

const onboardingFlowExecutionOrder: Record<OnboardingTooltipFlow, OnboardingTooltipId[]> = {
  [OnboardingTooltipFlow.Desktop]: [
    OnboardingTooltipId.AskQuestionButton,
    OnboardingTooltipId.FeedButton,
    OnboardingTooltipId.HeaderSearchBar,
    OnboardingTooltipId.UserProfileButton
  ],
  [OnboardingTooltipFlow.Mobile]: [
    OnboardingTooltipId.AskQuestionButtonMobile,
    OnboardingTooltipId.FeedButtonMobile,
    OnboardingTooltipId.HeaderSearchBarMobile,
    OnboardingTooltipId.UserProfileButtonMobile
  ],
  [OnboardingTooltipFlow.UserEditDesktopMobile]: [ OnboardingTooltipId.ProfileEditCard ],
  [OnboardingTooltipFlow.ConversationDesktop]: [ OnboardingTooltipId.ConversationView ],
  [OnboardingTooltipFlow.ConversationMobile]: [ OnboardingTooltipId.ConversationViewMobile ]
}

export const useOnboardingTooltipsStore = defineStore('onboardingTooltips', () => {
  const isShowingTooltips = ref(false)
  const currentTooltipIndex = ref(0)
  const currentTooltipFlow = ref(OnboardingTooltipFlow.Desktop)
  const overlayCutoutDimensions = ref<Record<OnboardingTooltipId, DOMRect>>({
    AskQuestionButton: getDOMRect(),
    FeedButton: getDOMRect(),
    HeaderSearchBar: getDOMRect(),
    UserProfileButton: getDOMRect(),
    AskQuestionButtonMobile: getDOMRect(),
    FeedButtonMobile: getDOMRect(),
    HeaderSearchBarMobile: getDOMRect(),
    UserProfileButtonMobile: getDOMRect(),
    ProfileEditCard: getDOMRect(),
    ConversationView: getDOMRect(),
    ConversationViewMobile: getDOMRect()
  })

  const currentTooltipId = computed(() => (isShowingTooltips.value
    ? onboardingFlowExecutionOrder[currentTooltipFlow.value][currentTooltipIndex.value]
    : undefined))

  const overlayCutoutDimensionsOfCurrentTooltip = computed(() => {
    const id = currentTooltipId.value
    return id ? overlayCutoutDimensions.value[id] : new DOMRect()
  })

  const totalTooltipsOfCurrentFlow = computed(() => onboardingFlowExecutionOrder[currentTooltipFlow.value].length)

  function startOnboardingTooltipsManually() {
    currentTooltipFlow.value = OnboardingTooltipFlow.Desktop
    start()
  }
  function nextOnboardingTooltip() {
    if (currentTooltipIndex.value >= totalTooltipsOfCurrentFlow.value - 1) {
      isShowingTooltips.value = false
    } else {
      next()
    }
  }

  async function startEducationalTooltipFlow(flow: OnboardingTooltipFlow) {
    if (!isShowingTooltips.value && !getWindowObject().Cypress) {
      currentTooltipFlow.value = flow
      start()
    }
  }

  function nextEducationalTooltip() {
    if (currentTooltipIndex.value >= totalTooltipsOfCurrentFlow.value - 1) {
      isShowingTooltips.value = false
    } else {
      next()
    }
  }

  function start() {
    isShowingTooltips.value = true
    currentTooltipIndex.value = 0
  }

  function next() {
    currentTooltipIndex.value = (currentTooltipIndex.value + 1) % totalTooltipsOfCurrentFlow.value
  }

  function updateOverlayCutout({ dimensions, tooltipId }: IUpdateOverlayCutoutPayload) {
    overlayCutoutDimensions.value[tooltipId] = dimensions
  }

  function updateBreakpoint(innerWidth: number) {
    if ([ OnboardingTooltipFlow.Desktop, OnboardingTooltipFlow.Mobile ].includes(currentTooltipFlow.value)) {
      currentTooltipFlow.value = innerWidth < 960 ? OnboardingTooltipFlow.Mobile : OnboardingTooltipFlow.Desktop
    }
  }

  return {
    isShowingTooltips,
    currentTooltipIndex,
    currentTooltipFlow,
    overlayCutoutDimensions,
    currentTooltipId,
    overlayCutoutDimensionsOfCurrentTooltip,
    totalTooltipsOfCurrentFlow,
    startOnboardingTooltipsManually,
    nextOnboardingTooltip,
    startEducationalTooltipFlow,
    nextEducationalTooltip,
    updateOverlayCutout,
    updateBreakpoint
  }
})
